import { Skeleton } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { CpWidgetVariantType } from 'api/customer-portal/types';
import { CpSectionWrapper } from 'components/organisms/customer-portal/widgets/common';
import {
  ResourceCard as RC,
  ResourcesGrid,
} from 'components/organisms/customer-portal/widgets/resources/variants/cp-resources-grid';
import {
  MaximizedResourceCard,
  MinimizedResourceCard,
  ResourceContentWrapper,
  ResourceList,
  ResourceSectionList,
  ResourceSectionWrapper,
} from 'components/organisms/customer-portal/widgets/resources/variants/cp-resources-list';
import { CpResourcesScrollableGridWrapper } from 'components/organisms/customer-portal/widgets/resources/variants/cp-resources-scrollable-grid';
import { useIsResponsive, useIsTablet } from 'hooks/use-media-query';
import { CpLoadingProps, TitleAndDesc } from '../common';

const ResourceCard = styled(RC)`
  max-width: 100%;
`;

const ResourceImage = styled(Skeleton.Image)`
  /**
    margin-bottom: 0;
  width: 100%;
  height: 200px;
  object-fit: cover; */

  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 294px !important;
    height: 200px !important;

    @media (max-width: 480px) {
      width: 264px !important;
    }
  }

  &.ant-skeleton-image,
  .ant-skeleton-image {
    border-radius: var(--border-radius-default);
    height: 100% !important;
    width: 100% !important;
  }
`;

const BigResourceImage = styled(Skeleton.Image)`
  /**
    margin-bottom: 0;
  width: 100%;
  height: 200px;
  object-fit: cover; */

  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 450px !important;
    height: 200px !important;

    @media (max-width: 480px) {
      width: 264px !important;
    }
  }

  &.ant-skeleton-image,
  .ant-skeleton-image {
    border-radius: var(--border-radius-default);
    height: 100% !important;
    width: 100% !important;
  }
`;

const ResourceName = styled(Skeleton.Button)`
  /**
    margin-bottom: 0;
  width: 100%;
  height: 200px;
  object-fit: cover; */

  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 157px !important;
    height: 29px !important;
  }

  &.ant-skeleton-button,
  .ant-skeleton-button {
    border-radius: var(--border-radius-default);
    height: 100% !important;
    width: 100% !important;
  }
`;

const ResourceDescription = styled(Skeleton.Button)`
  /**
    margin-bottom: 0;
  width: 100%;
  height: 200px;
  object-fit: cover; */

  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 294px !important;
    height: 76px !important;
    /* margin-top: var(--cp-spacing-04); */

    @media (max-width: 480px) {
      width: 264px !important;
    }
  }

  &.ant-skeleton-button,
  .ant-skeleton-button {
    border-radius: var(--border-radius-default);
    height: 100% !important;
    width: 100% !important;
  }
`;

const ResourceCtaLink = styled(Skeleton.Button)`
  /**
    margin-bottom: 0;
  width: 100%;
  height: 200px;
  object-fit: cover; */

  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 76px !important;
    height: 19px !important;
    /* margin-top: var(--cp-spacing-05); */
  }

  &.ant-skeleton-button,
  .ant-skeleton-button {
    border-radius: var(--border-radius-default);
    height: 100% !important;
    width: 100% !important;
  }
`;

export const CpResourcesLoadingCard: React.FC = () => (
  <ResourceCard>
    <ResourceImage />
    <ResourceName active />
    <ResourceDescription active />
    <ResourceCtaLink active />
  </ResourceCard>
);

const StyledMiniResourceCard = styled(MinimizedResourceCard)`
  &:first-child {
    padding-top: 0;
  }
`;

const Content: React.FC<{
  variantType: CpWidgetVariantType;
  input: Record<string, unknown>;
}> = ({ variantType, input }) => {
  const isTablet = useIsTablet();
  const isResponsive = useIsResponsive();

  switch (variantType) {
    case CpWidgetVariantType.ResourcesScrollableGrid:
      return (
        <CpResourcesScrollableGridWrapper>
          <Swiper
            freeMode
            pagination={{ clickable: true }}
            spaceBetween={24}
            className="mySwiper"
          >
            {((input?.resources as Array<unknown>) ?? [1, 2, 3, 4, 5]).map?.(
              (_e, i) => (
                <SwiperSlide key={i.toString()}>
                  <CpResourcesLoadingCard />
                </SwiperSlide>
              ),
            )}
          </Swiper>
        </CpResourcesScrollableGridWrapper>
      );

    case CpWidgetVariantType.ResourcesGrid:
      return (
        <ResourcesGrid>
          {[1, 2, 3, 4, 5]?.map(() => (
            <CpResourcesLoadingCard />
          ))}
        </ResourcesGrid>
      );

    case CpWidgetVariantType.ResourcesList:
      return (
        <>
          {isResponsive || isTablet ? (
            <ResourceSectionList>
              {((input?.resources as Array<unknown>) ?? [1, 2, 3, 4])?.map(
                () => (
                  <CpResourcesLoadingCard />
                ),
              )}
            </ResourceSectionList>
          ) : (
            <ResourceSectionWrapper className="cp-mt-[var(--cp-spacing-09)]">
              <MaximizedResourceCard>
                <BigResourceImage />
                <ResourceContentWrapper>
                  <ResourceName active />
                  <ResourceDescription active />
                </ResourceContentWrapper>
              </MaximizedResourceCard>

              <ResourceList>
                {((input?.resources as unknown[]) ?? [1, 2, 3, 4, 5])
                  ?.slice(1, (input?.resources as unknown[])?.length ?? 5)
                  .map(() => (
                    <>
                      <StyledMiniResourceCard>
                        <ResourceImage />
                        <ResourceContentWrapper>
                          <ResourceName active />
                          <ResourceDescription active />
                        </ResourceContentWrapper>
                      </StyledMiniResourceCard>
                    </>
                  ))}
              </ResourceList>
            </ResourceSectionWrapper>
          )}
        </>
      );

    default:
      return <></>;
  }
};

export const CpResourcesLoading: React.FC<CpLoadingProps> = ({
  input,
  styles,
  variantType,
}) => {
  return (
    <CpSectionWrapper
      background={styles?.background as string}
      paddingY={styles?.paddingY as number | undefined}
      className="!cp-max-w-none"
    >
      <TitleAndDesc
        title={input?.title as string}
        desc={input?.description as string}
      />

      <Content variantType={variantType} input={input} />
    </CpSectionWrapper>
  );
};
